/**
 * JAISIU-4497 — open a Fleet Control chat in a new browser tab.
 *
 * The chat history sidebar renders each chat as a `<button>` that switches
 * the active session in place. Standard browser semantics say a middle-click
 * (mouse wheel button) on a link opens the target in a new tab; operators
 * reasonably expect the same for a chat row. This module holds the two
 * pure pieces of that behaviour so they can be unit-tested without a DOM:
 *
 *  1. `buildChatTabUrl()` — the deep link for a session
 *     (`<basePath>/chat?session=<encoded session key>`).
 *  2. `shouldOpenInNewTab()` — decides whether a mouse event means
 *     "open in a new tab" (middle button, or Ctrl/Cmd + left click).
 *
 * The Control UI already understands `?session=<key>`:
 * `app-settings.ts` → `applySettingsFromUrl()` reads it and applies the
 * session as active. The same URL shape is already used elsewhere for
 * cross-view deep links (`views/sessions.ts`, `views/cron.ts`).
 */

/**
 * Build the Control UI URL that opens `sessionKey` in the chat view.
 *
 * `basePath` is the path prefix the Control UI is served under (empty for
 * a root deployment, e.g. `/apps/jaisiu` for a prefixed one). It is passed
 * through `pathForTab` so the prefix handling stays in one place.
 *
 * The session key is URL-encoded: session keys can contain `:`/`/` and
 * fork UUIDs, which are not safe in a query value.
 */
export function buildChatTabUrl(
  sessionKey: string,
  pathForTab: (tab: "chat", basePath: string) => string,
  basePath = "",
): string {
  return `${pathForTab("chat", basePath)}?session=${encodeURIComponent(sessionKey)}`;
}

/**
 * True when the mouse event should open the chat in a NEW browser tab.
 *
 * - Middle button (`button === 1`): the "mouse wheel" click. This is the
 *   primary affordance the operator asked for.
 * - Ctrl/Cmd + left click (`button === 0` with a modifier): standard
 *   link semantics in every browser (Cmd on macOS, Ctrl elsewhere).
 *
 * Right click (`button === 2`) is deliberately excluded — that opens the
 * browser context menu, and `auxclick` fires for it too.
 */
export function shouldOpenInNewTab(event: {
  button?: number;
  ctrlKey?: boolean;
  metaKey?: boolean;
}): boolean {
  const button = event.button ?? 0;
  if (button === 1) {
    return true;
  }
  if (button !== 0) {
    return false;
  }
  return event.ctrlKey === true || event.metaKey === true;
}
